import type { BlogCategoryResponseDto, BlogPublicCategoryNodeDto } from '@/api';

export interface BlogCategoryTreeNode {
  category: BlogCategoryResponseDto;
  children: BlogCategoryTreeNode[];
}

export function normalizeBlogCategoryParentId(
  raw: BlogCategoryResponseDto['parentId'],
): number | null {
  if (raw == null) {
    return null;
  }
  if (typeof raw === 'number') {
    return Number.isFinite(raw) ? raw : null;
  }
  const n = Number(raw);
  return Number.isFinite(n) ? n : null;
}

export function buildBlogCategoryTree(flat: BlogCategoryResponseDto[]): BlogCategoryTreeNode[] {
  const byId = new Map<number, BlogCategoryTreeNode>();
  for (const c of flat) {
    byId.set(c.id, { category: c, children: [] });
  }

  const roots: BlogCategoryTreeNode[] = [];
  for (const c of flat) {
    const node = byId.get(c.id);
    if (!node) continue;

    const pid = normalizeBlogCategoryParentId(c.parentId);
    if (pid == null) {
      roots.push(node);
      continue;
    }

    const parent = byId.get(pid);
    if (parent) {
      parent.children.push(node);
    } else {
      roots.push(node);
    }
  }

  const sortSiblings = (a: BlogCategoryTreeNode, b: BlogCategoryTreeNode): number => {
    const o = a.category.sortOrder - b.category.sortOrder;
    return o !== 0 ? o : a.category.id - b.category.id;
  };

  roots.sort(sortSiblings);
  for (const n of byId.values()) {
    n.children.sort(sortSiblings);
  }

  return roots;
}

/** Slugs of ancestors from root to parent (excludes `slug` itself). */
export function findBlogCategoryAncestors(
  slug: string,
  roots: BlogPublicCategoryNodeDto[],
): string[] {
  const ancestors: string[] = [];

  const walk = (nodes: BlogPublicCategoryNodeDto[], path: string[]): boolean => {
    for (const node of nodes) {
      const nextPath = [...path, node.slug];
      if (node.slug === slug) {
        ancestors.push(...path);
        return true;
      }
      if (node.children.length > 0 && walk(node.children, nextPath)) {
        return true;
      }
    }
    return false;
  };

  walk(roots, []);
  return ancestors;
}

const BLOG_CATEGORY_OPTION_INDENT = '\u00A0\u00A0';

export interface BlogCategorySelectOption {
  id: number;
  name: string;
  slug: string;
  depth: number;
  isLeaf: boolean;
  selectable: boolean;
  label: string;
}

export function formatBlogCategoryOptionLabel(name: string, depth: number): string {
  const indent = BLOG_CATEGORY_OPTION_INDENT.repeat(depth);
  const prefix = depth >= 1 ? '— ' : '';
  return `${indent}${prefix}${name}`;
}

export function flattenBlogCategoryTreeForSelect(
  roots: BlogCategoryTreeNode[],
  options?: { mapSelectable?: (row: BlogCategorySelectOption) => boolean },
): BlogCategorySelectOption[] {
  const mapSelectable = options?.mapSelectable;
  const out: BlogCategorySelectOption[] = [];

  const visit = (nodes: BlogCategoryTreeNode[], depth: number) => {
    for (const n of nodes) {
      const c = n.category;
      const row: BlogCategorySelectOption = {
        id: c.id,
        name: c.name,
        slug: c.slug,
        depth,
        isLeaf: c.isLeaf,
        label: formatBlogCategoryOptionLabel(c.name, depth),
        selectable: true,
      };
      row.selectable = mapSelectable ? mapSelectable(row) : true;
      out.push(row);
      if (n.children.length > 0) {
        visit(n.children, depth + 1);
      }
    }
  };

  visit(roots, 0);
  return out;
}

export function flattenPublicCategoryNodes(
  roots: BlogPublicCategoryNodeDto[],
): BlogPublicCategoryNodeDto[] {
  const out: BlogPublicCategoryNodeDto[] = [];

  const visit = (nodes: BlogPublicCategoryNodeDto[]) => {
    for (const node of nodes) {
      out.push(node);
      if (node.children.length > 0) {
        visit(node.children);
      }
    }
  };

  visit(roots);
  return out;
}
